import type {ReactElement} from 'react';
import React, {useRef, useState, useEffect} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {TimelineRefsProvider} from '@Components/poster-editor/components/timeline/timeline-context';
import {useTimelineRefs} from '@Components/poster-editor/components/timeline/hooks/useTimelineRefs';
import {TimelineRuler} from '@Components/poster-editor/components/timeline/components/timeline-ruler/timeline-ruler';
import {PageTimelineTrack} from '@Components/poster-editor/components/timeline/components/page-timeline-track/page-timeline-track';
import {AudioTimelineTrack} from '@Components/poster-editor/components/timeline/components/audio-timeline-track/audio-timeline-track';
import {TranscriptTimelineTrack} from '@Components/poster-editor/components/timeline/components/transcript-timeline-track/transcript-timeline-track';
import {getCurrentPageDuration, getCurrentPageTime} from '@Components/poster-editor/poster-editor-reducer';
import {updateTimelineWidthPerSecond} from '@Components/poster-editor/components/timeline/timeline-reducer';
import {useTimelineWidthPerSecond} from '@Components/poster-editor/components/timeline/hooks/timeline-hooks';
import type {RootState} from '@/store';
import styles from './mobile-timeline.module.scss';

function MobileTimelineContent(): ReactElement | null {
  const {scrollContainerRef} = useTimelineRefs();
  const dispatch = useAppDispatch();

  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const widthPerSecond = useTimelineWidthPerSecond();

  const widthPerSecondRef = useRef(widthPerSecond);
  widthPerSecondRef.current = widthPerSecond;

  const currentPageDurationRef = useRef(currentPageDuration);
  currentPageDurationRef.current = currentPageDuration;

  const isSyncingRef = useRef(false);
  const isPinchingRef = useRef(false);
  const pinchStartDistanceRef = useRef(0);
  const pinchStartWidthPerSecondRef = useRef(widthPerSecond);
  const prevRelativeTimeRef = useRef<number | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const halfPadding = containerWidth > 0 ? containerWidth / 2 : 300;

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const observer = new ResizeObserver(() => {
      setContainerWidth(el.clientWidth);
    });
    observer.observe(el);
    return (): void => {
      observer.disconnect();
    };
  }, [scrollContainerRef]);

  // Instant scroll when zoom level changes
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }
    const state = window.PMW.redux.store.getState() as RootState;
    const time = getCurrentPageTime(state);
    isSyncingRef.current = true;
    el.scrollLeft = time * widthPerSecond;
    setTimeout((): void => {
      isSyncingRef.current = false;
    }, 0);
  }, [widthPerSecond, scrollContainerRef]);

  // Sync scroll to playhead via store subscription to avoid re-rendering tracks
  useEffect(() => {
    let prevTime: number | null = prevRelativeTimeRef.current;
    const unsubscribe = window.PMW.redux.store.subscribe(() => {
      const el = scrollContainerRef.current;
      if (!el) {
        return;
      }
      const state = window.PMW.redux.store.getState() as RootState;
      const time = getCurrentPageTime(state);
      if (time === prevTime) {
        return;
      }
      const isLargeJump = prevTime === null || Math.abs(time - prevTime) > 0.3;
      prevTime = time;
      prevRelativeTimeRef.current = time;

      isSyncingRef.current = true;
      if (isLargeJump) {
        el.scrollTo({left: time * widthPerSecondRef.current, behavior: 'smooth'});
        setTimeout((): void => {
          isSyncingRef.current = false;
        }, 400);
      } else {
        el.scrollLeft = time * widthPerSecondRef.current;
        setTimeout((): void => {
          isSyncingRef.current = false;
        }, 50);
      }
    });
    return (): void => {
      unsubscribe();
    };
  }, [scrollContainerRef]);

  const lastSeekTimeRef = useRef(0);
  const pendingRafRef = useRef<number | null>(null);
  const handleScroll = (): void => {
    if (isSyncingRef.current) {
      return;
    }
    const now = performance.now();
    const doSeek = (): void => {
      lastSeekTimeRef.current = performance.now();
      const el = scrollContainerRef.current;
      const pageDuration = currentPageDurationRef.current;
      if (!el || pageDuration === undefined) {
        return;
      }
      const seekRelativeTime = Math.min(Math.max(0, el.scrollLeft / widthPerSecondRef.current), pageDuration);
      void window.posterEditor?.whiteboard?.getCurrentPage()?.seek(seekRelativeTime);
    };

    if (now - lastSeekTimeRef.current >= 200) {
      doSeek();
    } else if (pendingRafRef.current === null) {
      pendingRafRef.current = requestAnimationFrame(() => {
        pendingRafRef.current = null;
        doSeek();
      });
    }
  };

  const handleTouchStart = (e: React.TouchEvent): void => {
    if (e.touches.length !== 2) {
      return;
    }
    isPinchingRef.current = true;
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    pinchStartDistanceRef.current = Math.sqrt(dx * dx + dy * dy);
    pinchStartWidthPerSecondRef.current = widthPerSecondRef.current;
  };

  const handleTouchMove = (e: React.TouchEvent): void => {
    if (!isPinchingRef.current || e.touches.length !== 2) {
      return;
    }
    e.preventDefault();
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const scale = distance / pinchStartDistanceRef.current;
    dispatch(updateTimelineWidthPerSecond(Math.round(pinchStartWidthPerSecondRef.current * scale)));
  };

  const handleTouchEnd = (): void => {
    isPinchingRef.current = false;
  };

  if (!currentPageDuration) {
    return null;
  }

  return (
    <div
      ref={scrollContainerRef}
      className={styles.scrollContainer}
      onScroll={handleScroll}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.content} style={{paddingLeft: `${halfPadding}px`, paddingRight: `${halfPadding}px`}}>
        <TimelineRuler />
        <div className={styles.tracksContainer}>
          <PageTimelineTrack />
          <AudioTimelineTrack />
          <TranscriptTimelineTrack />
        </div>
      </div>
    </div>
  );
}

export function MobileTimeline(): ReactElement {
  return (
    <TimelineRefsProvider>
      <MobileTimelineContent />
    </TimelineRefsProvider>
  );
}
